import React, { useState, useMemo } from 'react';
import { Card } from 'components/shadcn/ui/card';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import HGSuccessCard from 'components/HGSuccessCard';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import getValidationSchema from 'components/HGForm/formValidations';
import {
    addSpaceBeforeEveryCapitalLetter,
    stringifyAddressObject,
} from 'components/HGForm/helpers';
import HGFormInput from 'components/HGForm/HGFormInput';
import AddressFields from 'components/HGForm/AddressFields';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import CompanyDropdown from 'components/HGForm/CompanyDropdown';
import HGError from 'components/HGForm/HGError';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
    useVerifyAdmin,
    useVerifyInstaller,
    API_ENDPOINT,
    useJwtToken,
    logActivity,
} from 'helper';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQueryClient } from 'react-query';

const AddSites = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { user } = useAuthenticator((context) => [context.user]);
    const isAdmin = useVerifyAdmin();
    const isInstaller = useVerifyInstaller();
    const token = useJwtToken();
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [newSite, setNewSite] = useState(null);
    const initialFieldsValues = useMemo(
        () => ({
            address: {
                address: '',
                city: '',
                stateProvince: '',
                postalCode: '',
                country: 'United States',
            },
            name: '',
            customerEmail: '',
            confirmCustomerEmail: '',

            ...(isAdmin && {
                installerEmail: '',
                confirmInstallerEmail: '',
                company: '',
            }),
        }),
        [isAdmin]
    );

    const fieldKeysArray = Object.keys(initialFieldsValues);

    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);
            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray]);

    const addSite = async (formValues) => {
        setErrorMessage(null);
        setLoading(true);
        const { address, customerEmail, installerEmail, company, name } =
            formValues;
        const addressString = stringifyAddressObject(address);
        try {
            const input = {
                Site_ID: uuidv4(),
                Address: addressString,
                CustomerEmail: customerEmail?.toLowerCase(),
                Name: name,
                CompanyEmail: isInstaller
                    ? user.attributes['custom:company_email']
                    : company?.Email,
                InstallerEmail: isInstaller
                    ? user.attributes.email
                    : installerEmail,
                Company_ID: 'REQUIRED-THIS-WILL-BE-OVERWRITTEN-BY-LAMBDA',
            };

            const newSiteResponse = await createSite(input, token);
            if (!newSiteResponse) {
                throw new Error('Error creating site');
            }
            logActivity(
                'User',
                'Info',
                'Add site',
                `New site added ${input.Site_ID} successfully`,
                {
                    before: null,
                    after: input,
                }
            );
            setLoading(false);
            setNewSite(newSiteResponse);
            setSuccess(true);
            queryClient.invalidateQueries('sites');
        } catch (error) {
            console.error('Error creating site: ', error);
            logActivity(
                'User',
                'Error',
                'Add site',
                `Error adding site. ${error.message}`,
                {
                    before: null,
                    after: null,
                }
            );
            setErrorMessage(error.message);
            setLoading(false);
        }
    };

    if (loading)
        return (
            <Card className="space-y-5 p-5">
                <Skeleton className="h-6 w-32" />
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    {Array.from({ length: 6 }).map((_, idx) => (
                        <Skeleton key={idx} className="h-10 w-full" />
                    ))}
                </div>
                <Skeleton className="h-10 w-32" />
            </Card>
        );

    if (success)
        return (
            <HGSuccessCard
                buttons={[
                    {
                        onClick: () =>
                            navigate(
                                `/sites/${newSite.Site_ID}`,
                                {
                                    state: newSite,
                                }
                            ),
                        buttonText: 'go to site profile',
                    },
                ]}
            >
                The site has been added successfully!
                <br />
                click below to view the site profile.
            </HGSuccessCard>
        );

    return (
        <Card className="space-y-5 p-5">
            <h4 className="mb-3">Site Info</h4>
            <Formik
                initialValues={initialFieldsValues}
                validationSchema={validationSchema}
                onSubmit={addSite}
                validateOnBlur={false}
                // validateOnChange={false}
            >
                <Form>
                    <div className="my-5 flex flex-col gap-5">
                        {fieldKeysArray.map((key) => {
                            if (key === 'address' || key === 'company')
                                return null;
                            return (
                                <HGFormInput
                                    key={key}
                                    name={key}
                                    label={addSpaceBeforeEveryCapitalLetter(
                                        key === 'name' ? 'site Name' : key
                                    )}
                                    variant="standard"
                                    type={
                                        key.toLowerCase().includes('password')
                                            ? 'password'
                                            : 'text'
                                    }
                                />
                            );
                        })}
                        {isAdmin && <CompanyDropdown label="Company" />}
                        <AddressFields />
                    </div>
                    {errorMessage && (
                        <HGError errorMessage={errorMessage} sx={{ mb: 3 }} />
                    )}
                    <div className="mt-10 flex justify-end">
                        <HGFormSubmitBtn
                            loading={loading}
                            className="h-12 w-full"
                        >
                            Add Site
                        </HGFormSubmitBtn>
                    </div>
                </Form>
            </Formik>
        </Card>
    );
};

export default AddSites;

const createSite = async (input, token) => {
    const url = `${API_ENDPOINT}/sites/add-site`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: token,
            },
            body: JSON.stringify(input),
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Unknown error occurred');
        }
        return await response.json();
    } catch (error) {
        console.error('Error creating site:', error.mesage);
        throw error;
    }
};
